import { Capacitor } from '@capacitor/core';
import type { ProductDataType } from '@am3nd-org/pointcraft-types';
import {
  capSQLiteSet,
  SQLiteConnection,
  SQLiteDBConnection,
} from '@capacitor-community/sqlite';

import { safeJSONParse } from 'src/utils/safe-json';

import type { BusinessV3Item } from 'src/sections/admin-page/ListView/Businesses/types';

import { InventoryStats, SystemConfigType } from './types';

const INVENTORY_UPSERT_SQL =
  `INSERT OR REPLACE INTO inventory
   (product_id, identifier, name, item_category, stock, price, sold, deleted_at, data, variable_price_lookup_code)
   VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?)` as const;

function toDeletedAtStr(deletedAt: ProductDataType['deletedAt']): string | null {
  if (!deletedAt) return null;
  if (deletedAt instanceof Date) return deletedAt.toISOString();
  return String(deletedAt);
}

function buildInventoryValues(product: ProductDataType): unknown[] {
  return [
    product.id,
    product.identifier ?? '',
    product.name ?? '',
    product.itemCategory ?? 'GENERAL',
    product.currentStockLevel ?? 0,
    product.price ?? 0,
    product.sold ? 1 : 0,
    toDeletedAtStr(product.deletedAt),
    JSON.stringify(product),
    product.attributes?.physicalGood?.variablePriceLookupCode ?? '',
  ];
}

function buildInventorySet(product: ProductDataType): capSQLiteSet {
  return {
    statement: INVENTORY_UPSERT_SQL,
    values: buildInventoryValues(product),
  };
}

class SQLiteService {
  private sqliteConnection: SQLiteConnection;

  private db: SQLiteDBConnection | null = null;

  private initialized = false;

  // The SQLiteService is constructed with an open SQLiteDBConnection and the SQLiteConnection manager.
  constructor(sqliteConnection: SQLiteConnection, dbConnection: SQLiteDBConnection) {
    console.log('SQLiteService constructor – injecting DB connection');
    this.sqliteConnection = sqliteConnection;
    this.db = dbConnection;
    this.initialized = true;
    this.addReloadEventListeners();

    // Ensure both tables exist (idempotent)
    this.checkAndCreateDatabase().catch((err) => {
      console.error('Error in checkAndCreateDatabase during initialization:', err);
    });
  }

  // Ensure all tables and indexes exist in the SQLite DB
  private async checkAndCreateDatabase(): Promise<void> {
    try {
      if (!this.db) {
        throw new Error('Database connection is not available');
      }

      // system_config table
      const res = await this.db.query(
        'SELECT name FROM sqlite_master WHERE type="table" AND name="system_config"'
      );
      if (!res.values || res.values.length === 0) {
        await this.db.execute(
          'CREATE TABLE IF NOT EXISTS system_config ' +
            '(id INTEGER PRIMARY KEY AUTOINCREMENT, type TEXT UNIQUE, data JSON)'
        );
      }

      // inventory table
      await this.db.execute(
        `CREATE TABLE IF NOT EXISTS inventory (
          product_id    TEXT PRIMARY KEY,
          identifier    TEXT NOT NULL DEFAULT '',
          name          TEXT NOT NULL DEFAULT '',
          item_category TEXT NOT NULL DEFAULT 'GENERAL',
          stock         REAL NOT NULL DEFAULT 0,
          price         REAL NOT NULL DEFAULT 0,
          sold          INTEGER NOT NULL DEFAULT 0,
          deleted_at    TEXT,
          data          TEXT NOT NULL
        )`
      );

      // indexes for inventory
      await this.db.execute(
        'CREATE INDEX IF NOT EXISTS idx_inventory_name ON inventory(name)'
      );
      await this.db.execute(
        'CREATE INDEX IF NOT EXISTS idx_inventory_identifier ON inventory(identifier)'
      );
      await this.db.execute(
        'CREATE INDEX IF NOT EXISTS idx_inventory_category ON inventory(item_category)'
      );
      // Composite index speeds up the category-counts GROUP BY query
      await this.db.execute(
        'CREATE INDEX IF NOT EXISTS idx_inventory_category_active ON inventory(item_category, deleted_at)'
      );

      // Add variable_price_lookup_code column if it doesn't exist yet (idempotent)
      const colCheck = await this.db.query(
        `SELECT COUNT(*) as cnt FROM pragma_table_info('inventory') WHERE name = 'variable_price_lookup_code'`
      );
      if ((colCheck.values?.[0]?.cnt ?? 0) === 0) {
        await this.db.execute(
          `ALTER TABLE inventory ADD COLUMN variable_price_lookup_code TEXT NOT NULL DEFAULT ''`
        );
      }
      await this.db.execute(
        'CREATE INDEX IF NOT EXISTS idx_inventory_vlpc ON inventory(variable_price_lookup_code)'
      );

      // One-time backfill: populate variable_price_lookup_code from JSON data for existing rows.
      // Runs only for rows where the column is still empty (i.e. pre-migration imports).
      const toBackfill = await this.db.query(
        `SELECT product_id, data FROM inventory WHERE variable_price_lookup_code = ''`
      );
      if (toBackfill.values && toBackfill.values.length > 0) {
        console.log(`[SQLiteService] Backfilling variable_price_lookup_code for ${toBackfill.values.length} rows`);
        const backfillSet: capSQLiteSet[] = toBackfill.values
          .map((row: any) => {
            const parsed = safeJSONParse<any>(row.data, 'SQLite:backfill-vlpc');
            let vlpc = '';
            if (parsed.success) {
              const physicalGood = parsed.data?.attributes?.physicalGood;
              // Use the stored value if present, otherwise derive from identifier for variable-price items
              vlpc = physicalGood?.variablePriceLookupCode
                ?? (physicalGood?.variablePrice === true && (parsed.data?.identifier?.length ?? 0) >= 5
                  ? String(parsed.data.identifier).substring(0, 5)
                  : '');
            }
            return { productId: row.product_id as string, vlpc };
          })
          .filter(({ vlpc }) => vlpc !== '')
          .map(({ productId, vlpc }) => ({
            statement: `UPDATE inventory SET variable_price_lookup_code = ? WHERE product_id = ?`,
            values: [vlpc, productId],
          }));
        if (backfillSet.length > 0) {
          await this.db.executeSet(backfillSet, true);
        }
        console.log('[SQLiteService] variable_price_lookup_code backfill complete');
      }

      // business_inventory_stats — derived cache, recalculated from inventory
      await this.db.execute(
        `CREATE TABLE IF NOT EXISTS business_inventory_stats (
          id                   TEXT PRIMARY KEY,
          total_items          INTEGER NOT NULL DEFAULT 0,
          total_active_items   INTEGER NOT NULL DEFAULT 0,
          total_deleted_items  INTEGER NOT NULL DEFAULT 0,
          total_stock_quantity REAL    NOT NULL DEFAULT 0,
          category_counts      TEXT    NOT NULL DEFAULT '[]',
          last_calculated_at   TEXT    NOT NULL,
          inventory_version    INTEGER
        )`
      );
    } catch (error) {
      console.error('Error checking or creating database tables:', error);
    }
  }

  // If the app is reloaded or resumed and the connection was closed, reinitialize it
  public async initializeDB(): Promise<void> {
    if (this.initialized) {
      return;
    }
    const platform = Capacitor.getPlatform();
    if (platform !== 'ios') {
      throw new Error('Unsupported platform for SQLiteService.initializeDB');
    }
    try {
      // Re-create the SQLite connection and DB if it was closed
      const connName = 'mydbV2';
      const isConn = (await this.sqliteConnection.isConnection(connName, false)).result;
      if (isConn) {
        await this.sqliteConnection.closeAllConnections();
      }
      const dbConnection = await this.sqliteConnection.createConnection(
        connName,
        false,
        'no-encryption',
        1,
        false
      );
      await dbConnection.open();
      await this.checkAndCreateDatabase();
      this.db = dbConnection;
      this.initialized = true;
      console.log('SQLiteService reinitialized DB after reload');
    } catch (error) {
      console.error('Error re-initializing SQLite DB:', error);
      throw error;
    }
  }

  // Retrieve the database connection, initializing if necessary (e.g., after a reload)
  private async getDB(): Promise<SQLiteDBConnection> {
    if (!this.initialized || this.db === null) {
      await this.initializeDB();
    }
    if (!this.db) {
      throw new Error('Database connection is not available');
    }
    return this.db;
  }

  private addReloadEventListeners(): void {
    window.addEventListener('beforeunload', async () => {
      console.log('Window unloading – closing SQLite connection');
      await this.closeConnection();
    });
    window.addEventListener('load', async () => {
      console.log('Window loaded – reopening SQLite connection if needed');
      await this.reopenConnection();
    });
  }

  public async closeConnection(): Promise<void> {
    if (this.db) {
      try {
        console.log('Closing SQLite DB connection');
        await this.db.close();
        this.db = null;
        this.initialized = false;
        console.log('SQLite DB connection closed');
      } catch (error) {
        console.error('Error closing SQLite DB connection:', error);
      }
    }
    // Ensure all native connections are closed (especially if multiple were open)
    try {
      await this.sqliteConnection.closeAllConnections();
      console.log('All SQLite connections closed');
    } catch (error) {
      console.error('Error closing all SQLite connections:', error);
    }
  }

  private async reopenConnection(): Promise<void> {
    if (!this.initialized) {
      // Re-open the DB if it was closed
      await this.initializeDB();
    }
    // If already initialized, do nothing (connection is alive)
  }

  // ---------------------------------------------------------------------------
  // system_config CRUD
  // ---------------------------------------------------------------------------

  public async updateOrCreateSystemConfig<T>(type: string, data: T): Promise<void> {
    try {
      const db = await this.getDB();
      const selectQuery = 'SELECT id FROM system_config WHERE type = ?';
      const result = await db.query(selectQuery, [type]);
      if (result.values && result.values.length > 0) {
        const updateQuery = 'UPDATE system_config SET data = ? WHERE type = ?';
        await db.run(updateQuery, [JSON.stringify(data), type]);
      } else {
        const insertQuery = 'INSERT INTO system_config (type, data) VALUES (?, ?)';
        await db.run(insertQuery, [type, JSON.stringify(data)]);
      }
    } catch (error) {
      console.error('Error in updateOrCreateSystemConfig:', error);
    }
  }

  public async getSystemConfig<T>(type: string): Promise<SystemConfigType<T> | null> {
    try {
      const db = await this.getDB();
      const query = 'SELECT * FROM system_config WHERE type = ?';
      const res = await db.query(query, [type]);

      if (res.values && res.values.length > 0) {
        const row = res.values[0];

        // Safe JSON parsing with context
        const parseResult = safeJSONParse<T>(row.data, `SQLite:getSystemConfig:${type}`);

        if (!parseResult.success) {
          console.error(`Failed to parse system config '${type}':`, parseResult.error);
          // Log the corrupted data for debugging
          console.error('Corrupted data (first 200 chars):', row.data?.substring(0, 200));
          return null;
        }

        return {
          id: row.id,
          type: row.type,
          data: parseResult.data,
        };
      }
      return null;
    } catch (error) {
      console.error('Error in getSystemConfig:', error);
      return null;
    }
  }

  public async getAllSystemConfigs<T>(): Promise<SystemConfigType<T>[]> {
    try {
      const db = await this.getDB();
      const query = 'SELECT * FROM system_config';
      const res = await db.query(query);

      if (!res.values) return [];

      // Filter out configs with parse errors instead of failing completely
      const configs: SystemConfigType<T>[] = res.values.reduce<SystemConfigType<T>[]>(
        (acc, row) => {
          const parseResult = safeJSONParse<T>(row.data, `SQLite:getAllSystemConfigs:${row.type}`);

          if (parseResult.success) {
            acc.push({
              id: row.id,
              type: row.type,
              data: parseResult.data,
            });
          } else {
            // Log but continue with other configs
            console.error(`Skipping corrupted config '${row.type}':`, parseResult.error);
          }

          return acc;
        },
        []
      );

      return configs;
    } catch (error) {
      console.error('Error in getAllSystemConfigs:', error);
      return [];
    }
  }

  public async deleteSystemConfig(type: string): Promise<void> {
    try {
      const db = await this.getDB();
      await db.run('DELETE FROM system_config WHERE type = ?', [type]);
    } catch (error) {
      console.error('Error deleting system config:', error);
    }
  }

  public async deleteAllSystemConfigs(): Promise<void> {
    try {
      const db = await this.getDB();
      await db.run('DELETE FROM system_config');
    } catch (error) {
      console.error('Error deleting all system configs:', error);
    }
  }

  // ---------------------------------------------------------------------------
  // inventory table helpers
  // ---------------------------------------------------------------------------

  // Idempotent — tables are already created in checkAndCreateDatabase().
  public async ensureInventoryTable(): Promise<void> {
    await this.checkAndCreateDatabase();
  }

  public async upsertInventoryItem(product: ProductDataType): Promise<void> {
    try {
      const db = await this.getDB();
      await db.run(INVENTORY_UPSERT_SQL, buildInventoryValues(product));
    } catch (error) {
      console.error('Error in upsertInventoryItem:', error);
    }
  }

  // Uses executeSet to send all rows in a single Capacitor bridge call.
  public async bulkUpsertInventory(products: ProductDataType[]): Promise<void> {
    if (products.length === 0) return;
    try {
      const db = await this.getDB();
      const set: capSQLiteSet[] = products.map((p) => buildInventorySet(p));
      await db.executeSet(set, true); // true = wrap in transaction
    } catch (error) {
      console.error('Error in bulkUpsertInventory:', error);
      throw error;
    }
  }

  public async hardDeleteInventoryItem(productId: string): Promise<void> {
    try {
      const db = await this.getDB();
      await db.run('DELETE FROM inventory WHERE product_id = ?', [productId]);
    } catch (error) {
      console.error('Error in hardDeleteInventoryItem:', error);
    }
  }

  public async queryInventoryPage(params: {
    page: number;
    pageSize: number;
    search?: string;
    category?: string;
  }): Promise<{ items: ProductDataType[]; total: number }> {
    try {
      const db = await this.getDB();
      const { page, pageSize, search, category } = params;
      const offset = page * pageSize;
      // Empty search → match everything via '%'; non-empty → substring match
      const like = search ? `%${search}%` : '%';
      // 'all' or undefined → skip category filter via sentinel value
      const catParam = category && category !== 'all' ? category : 'all';

      const countRes = await db.query(
        `SELECT COUNT(*) as total FROM inventory
         WHERE deleted_at IS NULL
           AND (name LIKE ? OR identifier LIKE ?)
           AND (? = 'all' OR LOWER(TRIM(item_category)) = LOWER(TRIM(?)))`,
        [like, like, catParam, catParam]
      );
      const total: number = countRes.values?.[0]?.total ?? 0;

      const dataRes = await db.query(
        `SELECT data FROM inventory
         WHERE deleted_at IS NULL
           AND (name LIKE ? OR identifier LIKE ?)
           AND (? = 'all' OR LOWER(TRIM(item_category)) = LOWER(TRIM(?)))
         ORDER BY name COLLATE NOCASE ASC
         LIMIT ? OFFSET ?`,
        [like, like, catParam, catParam, pageSize, offset]
      );

      const items: ProductDataType[] = (dataRes.values ?? []).reduce<ProductDataType[]>(
        (acc, row) => {
          const parseResult = safeJSONParse<ProductDataType>(row.data, 'SQLite:queryInventoryPage');
          if (parseResult.success) acc.push(parseResult.data);
          return acc;
        },
        []
      );

      return { items, total };
    } catch (error) {
      console.error('Error in queryInventoryPage:', error);
      return { items: [], total: 0 };
    }
  }

  public async countInventory(): Promise<number> {
    try {
      const db = await this.getDB();
      const res = await db.query(
        'SELECT COUNT(*) as total FROM inventory WHERE deleted_at IS NULL'
      );
      return res.values?.[0]?.total ?? 0;
    } catch (error) {
      console.error('Error in countInventory:', error);
      return 0;
    }
  }

  public async sumInventoryStock(): Promise<number> {
    try {
      const db = await this.getDB();
      const res = await db.query(
        'SELECT SUM(stock) as total FROM inventory WHERE deleted_at IS NULL'
      );
      return res.values?.[0]?.total ?? 0;
    } catch (error) {
      console.error('Error in sumInventoryStock:', error);
      return 0;
    }
  }

  public async clearAllInventory(): Promise<void> {
    try {
      const db = await this.getDB();
      await db.run('DELETE FROM inventory');
    } catch (error) {
      console.error('Error in clearAllInventory:', error);
    }
  }

  public async getInventoryById(productId: string): Promise<ProductDataType | null> {
    try {
      const db = await this.getDB();
      const res = await db.query(
        'SELECT data FROM inventory WHERE product_id = ? AND deleted_at IS NULL',
        [productId]
      );
      if (!res.values || res.values.length === 0) return null;
      const parseResult = safeJSONParse<ProductDataType>(res.values[0].data, 'SQLite:getInventoryById');
      return parseResult.success ? parseResult.data : null;
    } catch (error) {
      console.error('Error in getInventoryById:', error);
      return null;
    }
  }

  public async getInventoryByIdentifier(identifier: string): Promise<ProductDataType | null> {
    try {
      const db = await this.getDB();
      const res = await db.query(
        'SELECT data FROM inventory WHERE identifier = ? AND deleted_at IS NULL LIMIT 1',
        [identifier]
      );
      if (!res.values || res.values.length === 0) return null;
      const parseResult = safeJSONParse<ProductDataType>(res.values[0].data, 'SQLite:getInventoryByIdentifier');
      return parseResult.success ? parseResult.data : null;
    } catch (error) {
      console.error('Error in getInventoryByIdentifier:', error);
      return null;
    }
  }

  public async getInventoryByVariablePriceLookupCode(lookupCode: string): Promise<ProductDataType | null> {
    try {
      const db = await this.getDB();
      const res = await db.query(
        'SELECT data FROM inventory WHERE variable_price_lookup_code = ? AND deleted_at IS NULL LIMIT 1',
        [lookupCode]
      );
      if (!res.values || res.values.length === 0) return null;
      const parseResult = safeJSONParse<ProductDataType>(res.values[0].data, 'SQLite:getInventoryByVariablePriceLookupCode');
      return parseResult.success ? parseResult.data : null;
    } catch (error) {
      console.error('Error in getInventoryByVariablePriceLookupCode:', error);
      return null;
    }
  }

  // ---------------------------------------------------------------------------
  // business_inventory_stats — derived cache
  // ---------------------------------------------------------------------------

  public async getInventoryCategoryCounts(): Promise<Array<{ category: string; count: number }>> {
    try {
      const db = await this.getDB();
      const res = await db.query(
        `SELECT
           COALESCE(NULLIF(TRIM(item_category), ''), 'GENERAL') AS category,
           COUNT(*) AS count
         FROM inventory
         WHERE deleted_at IS NULL
         GROUP BY COALESCE(NULLIF(TRIM(item_category), ''), 'GENERAL')
         ORDER BY category COLLATE NOCASE ASC`
      );
      return (res.values ?? []).map((row) => ({
        category: String(row.category),
        count: Number(row.count),
      }));
    } catch (error) {
      console.error('Error in getInventoryCategoryCounts:', error);
      return [];
    }
  }

  public async recalculateInventoryStats(): Promise<InventoryStats> {
    try {
      const db = await this.getDB();

      const [totalRes, activeRes, deletedRes, stockRes, categoryCounts] = await Promise.all([
        db.query('SELECT COUNT(*) as total FROM inventory'),
        db.query('SELECT COUNT(*) as total FROM inventory WHERE deleted_at IS NULL'),
        db.query('SELECT COUNT(*) as total FROM inventory WHERE deleted_at IS NOT NULL'),
        db.query('SELECT SUM(stock) as total FROM inventory WHERE deleted_at IS NULL'),
        this.getInventoryCategoryCounts(),
      ]);

      const stats: InventoryStats = {
        id: 'inventory_stats',
        totalItems: Number(totalRes.values?.[0]?.total ?? 0),
        totalActiveItems: Number(activeRes.values?.[0]?.total ?? 0),
        totalDeletedItems: Number(deletedRes.values?.[0]?.total ?? 0),
        totalStockQuantity: Number(stockRes.values?.[0]?.total ?? 0),
        categoryCounts,
        lastCalculatedAt: new Date().toISOString(),
      };

      await db.run(
        `INSERT OR REPLACE INTO business_inventory_stats
           (id, total_items, total_active_items, total_deleted_items,
            total_stock_quantity, category_counts, last_calculated_at)
         VALUES (?, ?, ?, ?, ?, ?, ?)`,
        [
          stats.id,
          stats.totalItems,
          stats.totalActiveItems,
          stats.totalDeletedItems,
          stats.totalStockQuantity,
          JSON.stringify(stats.categoryCounts),
          stats.lastCalculatedAt,
        ]
      );

      return stats;
    } catch (error) {
      console.error('Error in recalculateInventoryStats:', error);
      return {
        id: 'inventory_stats',
        totalItems: 0,
        totalActiveItems: 0,
        totalDeletedItems: 0,
        totalStockQuantity: 0,
        categoryCounts: [],
        lastCalculatedAt: new Date().toISOString(),
      };
    }
  }

  public async getInventoryStats(options?: {
    recalculateIfMissing?: boolean;
    forceRecalculate?: boolean;
  }): Promise<InventoryStats> {
    const { recalculateIfMissing = true, forceRecalculate = false } = options ?? {};

    if (forceRecalculate) {
      return this.recalculateInventoryStats();
    }

    try {
      const db = await this.getDB();
      const res = await db.query(
        `SELECT * FROM business_inventory_stats WHERE id = 'inventory_stats'`
      );

      if (res.values && res.values.length > 0) {
        const row = res.values[0];
        const parseResult = safeJSONParse<Array<{ category: string; count: number }>>(
          row.category_counts,
          'SQLite:getInventoryStats:category_counts'
        );
        return {
          id: row.id,
          totalItems: Number(row.total_items ?? 0),
          totalActiveItems: Number(row.total_active_items ?? 0),
          totalDeletedItems: Number(row.total_deleted_items ?? 0),
          totalStockQuantity: Number(row.total_stock_quantity ?? 0),
          categoryCounts: parseResult.success ? parseResult.data : [],
          lastCalculatedAt: row.last_calculated_at,
          inventoryVersion: row.inventory_version ?? undefined,
        };
      }

      if (recalculateIfMissing) {
        return await this.recalculateInventoryStats();
      }
    } catch (error) {
      console.error('Error in getInventoryStats:', error);
    }

    return {
      id: 'inventory_stats',
      totalItems: 0,
      totalActiveItems: 0,
      totalDeletedItems: 0,
      totalStockQuantity: 0,
      categoryCounts: [],
      lastCalculatedAt: new Date().toISOString(),
    };
  }

  // ---------------------------------------------------------------------------
  // admin_businesses — stubs (admin panel is web-only)
  // ---------------------------------------------------------------------------

  // eslint-disable-next-line class-methods-use-this
  public async bulkReplaceAdminBusinesses(_businesses: BusinessV3Item[]): Promise<void> {
    // no-op: admin panel runs in web browser only
  }

  // eslint-disable-next-line class-methods-use-this
  public async getAllAdminBusinesses(): Promise<BusinessV3Item[]> {
    return [];
  }

  // eslint-disable-next-line class-methods-use-this
  public async clearAdminBusinesses(): Promise<void> {
    // no-op: admin panel runs in web browser only
  }
}

export default SQLiteService;
